import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabaseClient';
import GroupTile from '../components/GroupTile';
import ProposePlayModal from '../components/ProposePlayModal';
import CreateGroupModal from '../components/CreateGroupModal';
import Header from '../components/Header';
import UpcomingPlayCard from '../components/UpcomingPlayCard';
import OpenProposalsSection from '../components/OpenProposalsSection';
import {
  GROUP_TYPES,
  GROUP_VISIBILITY,
  PLAY_TYPES,
  PLAY_FORMATS,
  MAX_DAYS_AHEAD,
  COLORS,
  SKILL_LEVELS
} from '../constants';
import { UpcomingPlayEmptyState } from '../components/UpcomingPlayCard';
import { formatTime } from '../utils/dateTimeUtils';
import HowItWorksModal from '../components/HowItWorksModal';
import Dashboard from '../components/Dashboard/Dashboard';
import OnboardingFlow from '../components/Onboarding/OnboardingFlow';
import GuidedTour from '../components/GuidedTour.jsx';
import TourWelcomeModal from '../components/TourWelcomeModal.jsx';
import FeedbackModal from '../components/FeedbackModal';

function HomePage() {
  const navigate = useNavigate();
  const { user, profile, loading, fetchProfile } = useAuth();
  const [isCreateGroupModalOpen, setIsCreateGroupModalOpen] = useState(false);
  const [showProposeModal, setShowProposeModal] = useState(false);
  const [showHowItWorksModal, setShowHowItWorksModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [isSendingEmails, setIsSendingEmails] = useState(false);
  
  const [groups, setGroups] = useState([]);
  const [upcomingPlays, setUpcomingPlays] = useState([]);
  const [openProposals, setOpenProposals] = useState([]);

  // Tour state
  const [showTourWelcome, setShowTourWelcome] = useState(false);
  const [runTour, setRunTour] = useState(false);

  // Form data for create group modal
  const [groupForm, setGroupForm] = useState({
    name: '',
    description: '',
    visibility: GROUP_VISIBILITY.PUBLIC,
    gender: GROUP_TYPES.MIXED,
    skillLevel: SKILL_LEVELS.RATING_4_0 // Use the constant value
  });

  // Handle group form changes
  const handleGroupFormChange = (field, value) => {
    setGroupForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle group form submission
  const handleGroupSubmit = async (e) => {
    e.preventDefault();
    setError(null); // Reset error state
    try {
      console.log('Attempting to create group with data:', groupForm);
      
      // Create the group using the stored procedure
      const { data: groupData, error: groupError } = await supabase
        .rpc('create_group_with_admin', {
          p_name: groupForm.name,
          p_description: groupForm.description || '',
          p_visibility: groupForm.visibility || 'public',
          p_gender: groupForm.gender || 'mixed',
          p_skill_level: groupForm.skillLevel,
          p_admin_id: user.id
        });

      if (groupError) {
        console.error('Error creating group:', groupError);
        
        // Handle rate limit errors specifically
        if (groupError.message && groupError.message.includes('RATE_LIMIT_EXCEEDED')) {
          setError('You have reached your limit of 3 groups per hour. Please try again later.');
        } else {
          setError(groupError.message || 'Failed to create group');
        }
        return;
      }

      console.log('Group created successfully:', groupData);

      // Reset form and close modal
      setGroupForm({
        name: '',
        description: '',
        visibility: GROUP_VISIBILITY.PUBLIC,
        gender: GROUP_TYPES.MIXED,
        skillLevel: SKILL_LEVELS.RATING_4_0 // Use the constant value
      });
      setIsCreateGroupModalOpen(false);
      
      // Show success message
      setSuccessMessage('Group created successfully!');
      setTimeout(() => setSuccessMessage(''), 5000);

      // Refresh groups
      await fetchGroups();
    } catch (error) {
      console.error('Error creating group:', error);
      
      // Handle rate limit errors specifically
      if (error.message && error.message.includes('RATE_LIMIT_EXCEEDED')) {
        setError('You have reached your limit of 3 groups per hour. Please try again later.');
      } else {
        setError(error.message || 'An unexpected error occurred while creating the group');
      }
    }
  };

  // Option tile component for radio button groups
  const optionTile = (name, value, currentValue, label) => (
    <button
      type="button"
      onClick={() => handleGroupFormChange(name, value)}
      className={`px-4 py-2 rounded-lg border ${
        currentValue === value
          ? 'bg-blue-50 border-blue-500 text-blue-700'
          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
      }`}
    >
      {label}
    </button>
  );

  // Form data for propose play modal
  const [proposePlayForm, setProposePlayForm] = useState({
    group: '',
    typeOfPlay: '',
    format: '',
    date: '',
    time: '',
    location: '',
    comments: '',
    maxPlayers: ''
  });

  // Handle form changes
  const handleProposePlayChange = (e) => {
    const { name, value } = e.target;
    setProposePlayForm(prev => {
      const newState = {
        ...prev,
        [name]: value
      };
      
      // Reset format and maxPlayers when changing play type
      if (name === 'typeOfPlay') {
        newState.format = '';
        newState.maxPlayers = '';
      }
      
      return newState;
    });
  };

  // Handle propose play submission
  const handleProposePlaySubmit = async (formData) => {
    console.log('Submitting proposal with data:', formData);
    let createdProposalId = null;
    
    try {
      // Validate group ID
      if (!formData.group) {
        throw new Error('No group selected');
      }

      // Verify user is member of the group
      const { data: membership, error: membershipError } = await supabase
        .from('group_members')
        .select('id')
        .eq('group_id', formData.group)
        .eq('user_id', user.id)
        .single();

      if (membershipError || !membership) {
        console.error('Membership check failed:', membershipError);
        throw new Error('You must be a member of the group to create a proposal');
      }

      // Create the proposal
      const { data: proposalData, error: proposalError } = await supabase
        .from('play_proposals')
        .insert([{
          group_id: formData.group,
          type: formData.typeOfPlay,
          format: PLAY_FORMATS.SINGLES, // Both types now default to Singles
          date: formData.date,
          time: formData.time,
          location: formData.location,
          comments: formData.comments,
          max_players: 2, // Both Match and Just for Fun are now Singles (2 players)
          proposer_id: user.id,
          status: 'open'
        }])
        .select()
        .single();

      if (proposalError) {
        console.error('Error creating proposal:', proposalError);
        
        // Handle rate limit errors specifically
        if (proposalError.message && proposalError.message.includes('RATE_LIMIT_EXCEEDED')) {
          throw new Error('You have reached your limit of 5 proposals per hour. Please try again later.');
        } else {
          throw proposalError;
        }
      }

      console.log('Proposal created successfully:', proposalData);
      createdProposalId = proposalData.id;

      // Add proposer as first participant with a delay to ensure proposal is created
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const { error: participantError } = await supabase
        .from('proposal_participants')
        .insert({
          proposal_id: proposalData.id,
          user_id: user.id,
          status: 'accepted'
        });

      if (participantError) {
        console.error('Error adding participant:', participantError);
        // Rollback proposal creation
        await supabase.from('play_proposals').delete().eq('id', proposalData.id);
        throw new Error('Failed to add you as participant. Please try again.');
      }

      console.log('Added proposer as participant');

      // Send notification emails to all group members
      setIsSendingEmails(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        const response = await fetch(
          `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-proposal-notification-email`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session?.access_token}`,
            },
            body: JSON.stringify({
              proposalId: proposalData.id
            })
          }
        );

        const result = await response.json();
        
        if (!response.ok || !result.success) {
          throw new Error(result.error || 'Failed to send notification emails');
        }

        console.log('Notification emails sent successfully');
        setSuccessMessage('🎾 Proposal created and notifications sent! Your group members have been emailed.');
      } catch (emailError) {
        console.error('Error sending notification emails:', emailError);
        
        // Rollback: Delete the proposal and participant
        await supabase.from('proposal_participants').delete().eq('proposal_id', proposalData.id);
        await supabase.from('play_proposals').delete().eq('id', proposalData.id);
        
        throw new Error('Failed to send notification emails. Proposal not created. Please try again.');
      } finally {
        setIsSendingEmails(false);
      }

      setShowProposeModal(false);
      // Reset form
      setProposePlayForm({
        group: '',
        typeOfPlay: '',
        format: '',
        date: '',
        time: '',
        location: '',
        comments: '',
        maxPlayers: ''
      });
      
      // Refresh proposals
      await fetchOpenProposals();
      await fetchUpcomingPlays();
    } catch (error) {
      console.error('Error creating play proposal:', error);
      setError(error.message || 'Failed to create play proposal. Please try again.');
      setIsSendingEmails(false);
    }
  };

  // Separate data fetching from subscriptions
  const [isSubscribed, setIsSubscribed] = useState(false);

  // Initial data fetch - separate from subscriptions
  useEffect(() => {
    if (!user?.id) {
      console.log('No user ID, skipping initial fetch');
      return;
    }

    let isMounted = true;
    console.log('Initial data fetch starting for user:', user.id);

    const fetchAllData = async () => {
      setIsLoading(true);
      try {
        if (isMounted) {
          await fetchGroups();
          await fetchUpcomingPlays();
          await fetchOpenProposals();
        }
      } catch (error) {
        console.error('Error in initial data fetch:', error);
        if (isMounted) {
          setError('Failed to fetch initial data');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchAllData();

    return () => {
      console.log('Cleanup: Initial fetch useEffect');
      isMounted = false;
    };
  }, [user?.id]);

  // Separate effect for subscriptions
  useEffect(() => {
    if (!user?.id || isSubscribed) {
      console.log('Skipping subscription setup:', { userId: user?.id, isSubscribed });
      return;
    }

    console.log('Setting up subscriptions');
    setIsSubscribed(true);

    const groupsChannel = supabase.channel('groups-changes');
    const proposalsChannel = supabase.channel('proposals-changes');

    const setupSubscriptions = async () => {
      // Groups subscription
      groupsChannel
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'groups'
        }, (payload) => {
          console.log('Groups change received:', payload);
          fetchGroups();
        })
        .subscribe((status) => {
          console.log('Groups subscription status:', status);
        });

      // Proposals subscription
      proposalsChannel
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'play_proposals'
        }, (payload) => {
          console.log('Proposals change received:', payload);
          fetchOpenProposals();
          fetchUpcomingPlays();
        })
        .subscribe((status) => {
          console.log('Proposals subscription status:', status);
        });
    };

    setupSubscriptions();

    return () => {
      console.log('Cleanup: Removing subscriptions');
      supabase.removeChannel(groupsChannel);
      supabase.removeChannel(proposalsChannel);
      setIsSubscribed(false);
    };
  }, [user?.id]);

  // Fetch groups
  const fetchGroups = async () => {
    if (!user?.id) {
      console.log('fetchGroups: No user ID, skipping');
      return;
    }

    try {
      console.log('fetchGroups: Starting fetch');
      
      // Get current date and time
      const now = new Date();
      const currentTime = now.toLocaleTimeString('en-US', { hour12: false });
      const today = now.toISOString().split('T')[0];

      // Get all groups where user is a member
      const { data: memberGroups, error: memberError } = await supabase
        .from('group_members')
        .select(`
          id,
          role,
          groups (
            id,
            name,
            description,
            visibility,
            gender,
            skill_level,
            created_at,
            admin_id
          )
        `)
        .eq('user_id', user.id);

      if (memberError) {
        console.error('fetchGroups: Error fetching members:', memberError);
        throw memberError;
      }

      if (!memberGroups || memberGroups.length === 0) {
        console.log('fetchGroups: No groups found');
        setGroups([]);
        return;
      }

      // Get member counts and open proposals for each group
      const groupsWithDetails = await Promise.all(
        memberGroups
          .filter(mg => mg.groups) // Filter out any null groups
          .map(async ({ groups: group, role }) => {
            // Get member count
            const { count: memberCount } = await supabase
              .from('group_members')
              .select('id', { count: 'exact' })
              .eq('group_id', group.id);

            // Get open proposals count
            const { data: proposals } = await supabase
              .from('play_proposals')
              .select(`
                id,
                max_players,
                proposal_participants (
                  status
                )
              `)
              .eq('group_id', group.id)
              .eq('status', 'open')
              .or(`date.gt.${today},and(date.eq.${today},time.gt.${currentTime})`);

            // Count only proposals that aren't full
            const openProposalsCount = proposals?.filter(p => {
              const acceptedCount = p.proposal_participants.filter(pp => pp.status === 'accepted').length;
              return acceptedCount < p.max_players;
            }).length || 0;

            // Get pending join requests count if user is admin
            let pendingRequestsCount = 0;
            if (group.admin_id === user.id) {
              const { count: requestCount } = await supabase
                .from('join_requests')
                .select('id', { count: 'exact' })
                .eq('group_id', group.id)
                .eq('status', 'pending');
              pendingRequestsCount = requestCount || 0;
            }

            return {
              ...group,
              role,
              memberCount: memberCount || 0,
              openProposals: openProposalsCount,
              visibility: group.visibility || 'public',
              isAdmin: group.admin_id === user.id,
              pendingRequests: pendingRequestsCount
            };
          })
      );

      console.log('fetchGroups: Successfully fetched', groupsWithDetails.length, 'groups');
      setGroups(groupsWithDetails);
    } catch (error) {
      console.error('fetchGroups: Error:', error);
      setError('Failed to fetch groups: ' + error.message);
    }
  };

  // Fetch upcoming plays
  const fetchUpcomingPlays = async () => {
    try {
      // Get all confirmed matches in a single query
      const { data: matches, error } = await supabase
        .from('play_proposals')
        .select(`
          *,
          groups (
            id,
            name
          ),
          proposal_participants (
            user_id,
            status
          )
        `)
        .eq('status', 'open')
        .gte('date', new Date().toISOString().split('T')[0])
        .order('date', { ascending: true });

      if (error) {
        console.error('Error fetching matches:', error);
        return;
      }

      console.log('All matches:', matches);

      // Filter for matches where:
      // 1. You're the proposer and someone accepted, or
      // 2. You accepted someone else's proposal
      const yourMatches = matches.filter(match => {
        const acceptedParticipants = match.proposal_participants.filter(p => p.status === 'accepted');
        
        // Case 1: You're the proposer
        if (match.proposer_id === user.id) {
          return acceptedParticipants.some(p => p.user_id !== user.id);
        }
        
        // Case 2: You're an accepted participant
        return acceptedParticipants.some(p => p.user_id === user.id);
      });

      console.log('Your matches:', yourMatches);

      if (yourMatches.length === 0) {
        setUpcomingPlays([]);
        return;
      }

      // Get all opponent IDs
      const opponentIds = yourMatches.map(match => {
        if (match.proposer_id === user.id) {
          // Find the accepted participant who isn't you
          const opponent = match.proposal_participants.find(
            p => p.status === 'accepted' && p.user_id !== user.id
          );
          return opponent ? opponent.user_id : null;
        } else {
          // The proposer is your opponent
          return match.proposer_id;
        }
      }).filter(Boolean);

      // Fetch opponent profiles
      const { data: opponents, error: profileError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, skill_level')
        .in('id', opponentIds);

      if (profileError) {
        console.error('Error fetching opponent profiles:', profileError);
        return;
      }

      console.log('Opponent profiles:', opponents);

      // Create opponent lookup map
      const opponentMap = new Map(
        opponents.map(o => [o.id, o])
      );

      // Format the plays
      const formattedPlays = yourMatches
        .map(match => {
          // Find opponent
          let opponentId;
          if (match.proposer_id === user.id) {
            const opponent = match.proposal_participants.find(
              p => p.status === 'accepted' && p.user_id !== user.id
            );
            opponentId = opponent ? opponent.user_id : null;
          } else {
            opponentId = match.proposer_id;
          }

          const opponent = opponentMap.get(opponentId);
          if (!opponent) return null;

          return {
            id: match.id,
            groupName: match.groups.name,
            playType: match.type,
            format: match.format,
            date: new Date(match.date).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            }),
            time: formatTime(match.time),
            location: match.location,
            opponent: {
              id: opponent.id,
              name: `${opponent.first_name} ${opponent.last_name}`.trim(),
              skillLevel: opponent.skill_level || 'Not specified'
            },
            status: 'confirmed'
          };
        })
        .filter(Boolean);

      console.log('Final formatted plays:', formattedPlays);
      setUpcomingPlays(formattedPlays);

    } catch (error) {
      console.error('Error in fetchUpcomingPlays:', error);
      setUpcomingPlays([]);
    }
  };

  // Fetch open proposals
  const fetchOpenProposals = async () => {
    try {
      // Get current date and time
      const now = new Date();
      const currentTime = now.toLocaleTimeString('en-US', { hour12: false });
      const today = now.toISOString().split('T')[0];

      const { data: proposals, error: propError } = await supabase
        .from('play_proposals')
        .select(`
          id,
          type,
          format,
          date,
          time,
          location,
          max_players,
          group:groups (
            name
          ),
          proposal_participants (
            user_id,
            status
          )
        `)
        .eq('status', 'open')
        .eq('proposer_id', user.id)
        .or(`date.gt.${today},and(date.eq.${today},time.gt.${currentTime})`)
        .order('date', { ascending: true });

      if (propError) throw propError;

      // Filter out proposals that are technically full but haven't been marked as confirmed
      const formattedProposals = proposals
        .filter(proposal => {
          const acceptedCount = proposal.proposal_participants.filter(p => p.status === 'accepted').length;
          return acceptedCount < proposal.max_players;
        })
        .map(proposal => ({
          id: proposal.id,
          type: proposal.type,
          format: proposal.format,
          date: new Date(proposal.date).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          }),
          time: formatTime(proposal.time),
          location: proposal.location,
          groupName: proposal.group.name,
          acceptedCount: proposal.proposal_participants.filter(p => p.status === 'accepted').length,
          maxPlayers: proposal.max_players,
          acceptedPlayers: [] // We can fetch this if needed
        }));

      setOpenProposals(formattedProposals);
    } catch (error) {
      console.error('Error fetching open proposals:', error);
      setError('Failed to fetch open proposals. Please try again.');
    }
  };

  // Loading skeleton for groups
  const GroupsSkeleton = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {[1, 2, 3].map((i) => (
        <div key={i} className="bg-white rounded-xl p-6 shadow-sm animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
          <div className="h-5 bg-gray-200 rounded w-1/2"></div>
        </div>
      ))}
    </div>
  );

  // Success message component
  const SuccessMessage = ({ message, onClose }) => (
    <div className="fixed top-4 right-4 bg-green-50 border-l-4 border-green-500 p-4 rounded shadow-lg z-50 animate-fade-in-down">
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3">
          <p className="text-sm text-green-700">{message}</p>
        </div>
        <div className="ml-auto pl-3">
          <div className="-mx-1.5 -my-1.5">
            <button
              onClick={onClose}
              className="inline-flex rounded-md p-1.5 text-green-500 hover:bg-green-100 focus:outline-none"
            >
              <span className="sr-only">Dismiss</span>
              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Add handleLeaveGroup function
  const handleLeaveGroup = async (groupId) => {
    setError(null);
    try {
      // Delete the group membership
      const { error: leaveError } = await supabase
        .from('group_members')
        .delete()
        .eq('group_id', groupId)
        .eq('user_id', user.id);

      if (leaveError) {
        throw leaveError;
      }

      // Show success message
      setSuccessMessage('Successfully left the group');
      setTimeout(() => setSuccessMessage(''), 5000);

      // Refresh groups list
      await fetchGroups();
    } catch (error) {
      console.error('Error leaving group:', error);
      setError(error.message || 'Failed to leave group. Please try again.');
    }
  };

  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isCheckingOnboarding, setIsCheckingOnboarding] = useState(true);

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (!user) return;

      try {
        // Check if user has completed onboarding
        const { data, error } = await supabase
          .from('profiles')
          .select('onboarding_completed')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('Error fetching profile:', error);
          throw error;
        }

        // Show onboarding only if onboarding not completed
        const needsOnboarding = !data || data.onboarding_completed !== true;
        
        setShowOnboarding(needsOnboarding);
      } catch (error) {
        console.error('Error checking onboarding status:', error);
        // If there's an error, show onboarding as a fallback
        setShowOnboarding(true);
      } finally {
        setIsCheckingOnboarding(false);
      }
    };

    checkOnboardingStatus();
  }, [user]);

  const handleOnboardingComplete = async (profileData) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          first_name: profileData.first_name,
          last_name: profileData.last_name,
          phone_number: profileData.phone_number,
          skill_level: profileData.skill_level,
          gender: profileData.gender,
          avatar_url: profileData.avatar_url,
          onboarding_completed: true
        })
        .eq('id', user.id);

      if (error) throw error;
      setShowOnboarding(false);
      
      // Refresh the profile data to include the new avatar
      await fetchProfile(user.id);
      
      // Show tour welcome modal for new users
      const seen = localStorage.getItem('tnow_tour_seen_v1');
      if (!seen) {
        // Longer delay to ensure DOM targets exist, especially on mobile
        setTimeout(() => {
          setShowTourWelcome(true);
        }, 1000);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
    }
  };

  // Tour welcome modal handlers
  const handleStartTour = () => {
    setShowTourWelcome(false);
    // Add delay to ensure mobile elements are fully rendered
    setTimeout(() => {
      setRunTour(true);
    }, 500);
  };

  const handleSkipTour = () => {
    setShowTourWelcome(false);
    localStorage.setItem('tnow_tour_seen_v1', '1');
  };

  const handleStartTourFromHeader = () => {
    setShowTourWelcome(true);
  };

  if (loading || isCheckingOnboarding) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4FFF7]">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#039855]"></div>
      </div>
    );
  }

  if (showOnboarding) {
    return (
      <OnboardingFlow
        onComplete={handleOnboardingComplete}
        initialProfileData={profile}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F8F8]">
      
      <Header 
        onStartTour={handleStartTourFromHeader} 
        isOnboarding={showOnboarding}
        isTourRunning={runTour || showTourWelcome}
      />

      {/* Success Message */}
      {successMessage && (
        <SuccessMessage 
          message={successMessage} 
          onClose={() => setSuccessMessage('')}
        />
      )}

      {/* Error Message */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded z-50" role="alert">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
          <button 
            onClick={() => setError(null)} 
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
          >
            <span className="sr-only">Close</span>
            <svg className="fill-current h-6 w-6 text-red-500" role="button" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
              <title>Close</title>
              <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z"/>
            </svg>
          </button>
        </div>
      )}

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 relative">
        {/* Feedback Button - Top Right on Desktop, Centered on Mobile */}
        <div className="absolute top-0 md:right-4 left-1/2 md:left-auto transform -translate-x-1/2 md:transform-none z-10">
          <button
            onClick={() => setShowFeedbackModal(true)}
            className="px-3 py-1.5 text-sm font-medium text-gray-600 border-2 border-gray-300 rounded-lg hover:bg-gray-50 hover:text-primary transition-colors flex items-center space-x-1.5 shadow-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <span>Send Feedback</span>
          </button>
        </div>

        {/* Company Slogan */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-2">
            Find & Play
            <br />
            <span className="text-primary">With More</span>
            <br />
            <span className="text-primary">Players</span>
            <br />
            In Seconds
          </h1>
          <p className="text-gray-600 mt-4">Connect with local tennis players, join groups and play matches.</p>
        </div>

        {/* Main Action Buttons */}
        <div className="max-w-md mx-auto space-y-4 mb-12">
          <button
            data-tour="propose"
            onClick={() => setShowProposeModal(true)}
            className="w-full py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primaryDark transition"
          >
            Propose New Play
          </button>
          <button
            data-tour="create-group"
            onClick={() => setIsCreateGroupModalOpen(true)}
            className="w-full py-3 border-2 border-primary text-primary font-semibold rounded-lg hover:bg-primary/5 transition"
          >
            Create Group
          </button>
          <button
            data-tour="join-group"
            onClick={() => navigate('/join-groups')}
            className="w-full py-3 border-2 border-primary text-primary font-semibold rounded-lg hover:bg-primary/5 transition"
          >
            Join Group
          </button>
        </div>

        {/* Groups Section */}
        <section className="mb-8">
          <div className="bg-primary/5 py-3 px-4 mb-6">
            <h2 className="text-xl font-bold text-textDark">Your Groups</h2>
          </div>

          {isLoading ? (
            <GroupsSkeleton />
          ) : groups.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <div className="mb-4">
                <svg className="w-16 h-16 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 48 48">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Groups Yet</h3>
              <p className="text-gray-600 mb-4">Join or create a group to start playing tennis with others.</p>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => setIsCreateGroupModalOpen(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primaryDark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
                >
                  Create Group
                </button>
                <button
                  onClick={() => navigate('/join-groups')}
                  className="inline-flex items-center px-4 py-2 border-2 border-primary rounded-md text-sm font-medium text-primary hover:bg-primary/5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
                >
                  Join Group
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {groups.map(group => (
                <GroupTile
                  key={group.id}
                  id={group.id}
                  name={group.name}
                  openProposals={group.openProposals}
                  memberCount={group.memberCount}
                  visibility={group.visibility}
                  isAdmin={group.isAdmin}
                  pendingRequests={group.pendingRequests}
                  onLeaveGroup={handleLeaveGroup}
                />
              ))}
            </div>
          )}
        </section>

        {/* Upcoming Play Section */}
        <section className="mb-12">
          <div className="bg-primary/5 py-3 px-4 mb-6">
            <h2 className="text-xl font-bold text-textDark">Upcoming Play</h2>
          </div>
          {upcomingPlays.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <div className="text-center">
                <div className="font-bold text-2xl">
                  <div className="text-black">No Upcoming</div>
                  <div className="text-[#0D9488]">Play</div>
                </div>
                <p className="text-gray-600 mt-4 mb-6">Join a group and propose a play or accept an open proposal.</p>
                <button
                  onClick={() => setShowProposeModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primaryDark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Propose New Play
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {upcomingPlays.map(play => (
                <UpcomingPlayCard 
                  key={play.id}
                  {...play}
                />
              ))}
            </div>
          )}
        </section>

        {/* Open Proposals Section */}
        <OpenProposalsSection 
          proposals={openProposals} 
          isLoading={isLoading}
          onProposalDeleted={fetchOpenProposals}
        />

        {/* Create Group Modal */}
        <CreateGroupModal
          show={isCreateGroupModalOpen}
          onClose={() => setIsCreateGroupModalOpen(false)}
          onSubmit={handleGroupSubmit}
          onChange={handleGroupFormChange}
          groupForm={groupForm}
          optionTile={optionTile}
        />

        {/* Propose Play Modal */}
        <ProposePlayModal
          show={showProposeModal}
          onClose={() => setShowProposeModal(false)}
          onSubmit={handleProposePlaySubmit}
          formData={proposePlayForm}
          onChange={handleProposePlayChange}
          groupOptions={groups.map(g => ({ id: g.id, name: g.name }))}
          userSkillLevel={profile?.skill_level}
          isSubmitting={isSendingEmails}
        />

        {/* How It Works Modal */}
        {showHowItWorksModal && (
          <HowItWorksModal onClose={() => setShowHowItWorksModal(false)} />
        )}

        {/* Feedback Modal */}
        <FeedbackModal
          show={showFeedbackModal}
          onClose={() => setShowFeedbackModal(false)}
        />
      </main>

      {/* Tour Welcome Modal */}
      <TourWelcomeModal 
        show={showTourWelcome} 
        onStartTour={handleStartTour} 
        onSkipTour={handleSkipTour} 
      />

      {/* Guided Tour */}
      <GuidedTour run={runTour} onClose={() => setRunTour(false)} />
    </div>
  );
}

export default HomePage; 